import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Crown, MapPin, BadgePercent, ArrowUpRight } from "lucide-react";
import { fadeUp, stagger, viewportOnce, EASE } from "@/lib/motion";
import { useFoundationStats } from "@/lib/foundation";
import WaitlistDialog from "@/components/WaitlistDialog";
import { trackWaitlistOpen } from "@/lib/analytics";

const PERKS = [
  {
    icon: BadgePercent,
    title: "Lifetime discount, locked forever",
    body: "Founding partners pay half the standard platform fee — for as long as your business stays on Book Away. No expiry, no fine print.",
  },
  {
    icon: MapPin,
    title: "Priority placement on the map",
    body: "Your open slots surface first in local search and map discovery, putting your chairs in front of clients before anyone else.",
  },
  {
    icon: Crown,
    title: "Founding Member status",
    body: "A permanent gold badge on your profile, dedicated white-glove onboarding, and a direct line to our team.",
  },
];

export default function Foundation() {
  const navigate = useNavigate();
  const { total, claimed, left, pulseKey } = useFoundationStats();
  const pct = total > 0 ? Math.round((claimed / total) * 100) : 0;
  // Sydney's twenty are gone — the section still renders the perks so people
  // understand the programme, but the CTA pivots to the general waitlist so
  // we capture the lead instead of dead-ending the visitor.
  const soldOut = total > 0 && left <= 0;
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const openGeneralWaitlist = () => {
    trackWaitlistOpen("Sydney", "foundation_sold_out");
    setWaitlistOpen(true);
  };

  // Flash a gold ring on the scarcity meter whenever a new founder signs
  // (pulseKey bumps). Kept short so it feels celebratory, not distracting.
  useEffect(() => {
    if (!pulseKey) return;
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), 1500);
    return () => clearTimeout(t);
  }, [pulseKey]);
  return (
    <section
      id="foundation"
      data-testid="foundation-section"
      className="relative z-10 bg-[#EDEAE4] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto max-w-[1600px]">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">Foundation Members</p>
            <motion.h2
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2724]"
            >
              Join the <span className="italic text-gold">First 20.</span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mt-8 max-w-md text-lg leading-relaxed text-[#6B6B6B]"
            >
              We're hand-picking the first twenty businesses to launch Book Away in
              your city. Foundation Members lock in a lifetime discount and
              permanent priority placement — a founding advantage that never
              resets.
            </motion.p>

            {/* Scarcity meter */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              data-testid="foundation-scarcity"
              data-sold-out={soldOut ? "true" : "false"}
              data-pulsing={pulsing ? "true" : "false"}
              className={`mt-10 max-w-md rounded-2xl border p-6 transition-shadow duration-700 ${
                soldOut
                  ? "border-gold/50 bg-gradient-to-br from-[#F7F5F2] to-[#EDEAE4]"
                  : "border-black/10 bg-[#F7F5F2]"
              } ${pulsing ? "shadow-[0_0_0_4px_rgba(201,168,95,0.35),0_20px_40px_-20px_rgba(201,168,95,0.5)]" : ""}`}
            >
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-[#2A2724]">
                  {soldOut
                    ? `All ${total} Foundation seats claimed`
                    : `${claimed} of ${total} claimed`}
                </span>
                <span
                  data-testid="foundation-spots-left"
                  className={`font-medium ${soldOut ? "text-[#8B6F1F]" : "text-gold"}`}
                >
                  {soldOut ? "Sydney sold out" : `Only ${left} spots left`}
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-black/10">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 1.1, ease: EASE }}
                  className="h-full rounded-full bg-gold"
                />
              </div>
              {soldOut && (
                <p
                  data-testid="foundation-sold-out-copy"
                  className="mt-4 text-sm leading-relaxed text-[#6B6B6B]"
                >
                  <span className="text-[#2A2724]">Foundation full.</span> Join
                  the general Sydney waitlist and we'll reach out the moment a
                  seat opens up or a second cohort launches.
                </p>
              )}
            </motion.div>

            <motion.button
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              data-testid={soldOut ? "foundation-waitlist-cta" : "foundation-join-cta"}
              onClick={soldOut ? openGeneralWaitlist : () => navigate("/join-network")}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627]"
            >
              {soldOut
                ? "Join the general Sydney waitlist"
                : "Join the Network to View Membership Options"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.button>
          </div>

          {/* Perks */}
          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-7 lg:pl-12"
          >
            {PERKS.map((p, i) => (
              <motion.div
                key={p.title}
                variants={fadeUp}
                className={`flex items-start gap-6 py-7 ${
                  i !== PERKS.length - 1 ? "border-b border-black/10" : ""
                }`}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                  <p.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-[#2A2724]">{p.title}</h3>
                  <p className="mt-2 max-w-lg leading-relaxed text-[#6B6B6B]">{p.body}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <WaitlistDialog
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        city="Sydney"
      />
    </section>
  );
}
